import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Copy, Users, Gift } from "lucide-react";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";

const ACCENT = "#EC4899";

/**
 * Widget « Mon code parrainage » — visible sur le Tableau de bord pour tous
 * les collaborateurs (admin / manager / gestionnaire / collaborateur).
 *
 * Un collaborateur peut avoir 0, 1 ou plusieurs codes parrainage associés
 * (contact_type=collaborator). Les infos affichées sont un miroir fidèle de
 * la ligne équivalente dans « Catégories → Codes Parrainage ».
 */

function statusMeta(p) {
  if (p.is_active) return { label: "Actif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (p.status === "upcoming") return { label: "À venir", cls: "bg-sky-50 text-sky-700 border-sky-200" };
  if (p.status === "expired") return { label: "Expiré", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" };
  if (p.status === "disabled") return { label: "Désactivé", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Inactif", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" };
}

function OfferChips({ offers }) {
  const list = Array.isArray(offers) ? offers : [];
  if (list.length === 0) return <span className="text-xs text-zinc-400">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {list.map((o, i) => {
        const isPct = o.discount_type === "percentage";
        const suffix = isPct ? "%" : "€";
        const val = Number(o.discount_value || 0).toLocaleString("fr-FR", {
          minimumFractionDigits: isPct ? 0 : 2,
          maximumFractionDigits: 2,
        });
        const names = Array.isArray(o.service_product_names) ? o.service_product_names.filter(Boolean) : [];
        const scope = names.length === 0
          ? "Tous services"
          : (names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", "));
        const label = (o.label || "").trim();
        const tooltip = `${label ? label + " · " : ""}${val} ${suffix} · ${names.length ? names.join(", ") : "Tous services"}`;
        return (
          <div
            key={o.id || `offer-${i}`}
            className="flex items-start gap-2 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-2 py-1 bg-zinc-50 w-full"
            title={tooltip}
          >
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: ACCENT }} />
            <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {label && <span className="text-zinc-700 font-medium break-words">{label}</span>}
              <span className="font-mono font-semibold whitespace-nowrap" style={{ color: ACCENT }}>{val} {suffix}</span>
              <span className="text-zinc-500 text-[10px] break-words w-full sm:w-auto">· {scope}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReferralCard({ code }) {
  const meta = statusMeta(code);
  const discountLabel = `${Number(code.discount_value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${code.discount_type === "percentage" ? "%" : "€"}`;
  const brandChips = (code.brand_names && code.brand_names.length
    ? code.brand_names.map((name, i) => ({ name, color: (code.brand_colors && code.brand_colors[i]) || ACCENT }))
    : (code.brand_name ? [{ name: code.brand_name, color: code.brand_color || ACCENT }] : []));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code.code || "");
      toast.success("Code copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors overflow-hidden"
      data-testid={`my-referral-code-${code.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm shrink-0"
            style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
          >
            <Users size={18} strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-zinc-900 tracking-wider hover:text-[#0055FF] max-w-full"
              title="Copier le code"
              data-testid={`my-referral-copy-${code.id}`}
            >
              <span className="truncate">{code.code}</span>
              <Copy size={12} className="text-zinc-400 shrink-0" />
            </button>
            {code.description && (
              <p
                className="text-xs text-zinc-500 mt-0.5 break-words leading-snug"
                title={code.description}
              >
                {code.description}
              </p>
            )}
          </div>
        </div>
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider shrink-0 ${meta.cls}`}
          data-testid={`my-referral-status-${code.id}`}
        >
          {meta.label}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <div>
          <p className="label-uppercase mb-1">Départements</p>
          {brandChips.length === 0 ? (
            <span className="text-zinc-500">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {brandChips.map((bc, i) => (
                <span
                  key={`${code.id}-b-${i}`}
                  className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50"
                >
                  <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bc.color }} />
                  {bc.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="label-uppercase mb-1">Activation</p>
          <p className="font-mono text-[11px] text-zinc-700">
            {formatDateFr(code.start_date) || "—"}
          </p>
        </div>
        <div>
          <p className="label-uppercase mb-1">Fin</p>
          <p className="font-mono text-[11px] text-zinc-700">
            {code.is_indefinite ? <span className="italic text-zinc-500">Indéterminée</span> : (formatDateFr(code.end_date) || "—")}
          </p>
        </div>
        <div>
          <p className="label-uppercase mb-1">Durée</p>
          <p className="font-mono text-[11px] text-zinc-700">
            {code.duration_days != null ? `${code.duration_days} j` : <span className="italic text-zinc-500">Indéterminée</span>}
          </p>
        </div>
        <div>
          <p className="label-uppercase mb-1">Remise</p>
          <p className="font-mono font-semibold text-sm" style={{ color: ACCENT }}>{discountLabel}</p>
        </div>
        <div className="col-span-2 sm:col-span-2 lg:col-span-3">
          <p className="label-uppercase mb-1">Offres</p>
          <OfferChips offers={code.offers} />
        </div>
      </div>
    </div>
  );
}

export default function MyReferralCodesWidget() {
  const [codes, setCodes] = useState(null); // null = loading, [] = loaded empty
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/referral-codes/mine");
        if (!cancelled) setCodes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) { setErr(true); setCodes([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (codes === null) return null; // silent loading (aligns with other widgets)

  return (
    <div
      className="bg-white border border-zinc-200 p-6 rounded-sm"
      data-testid="my-referral-code-section"
    >
      <div className="mb-5 flex items-center gap-2">
        <span
          className="h-9 w-9 inline-flex items-center justify-center rounded-sm"
          style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
        >
          <Gift size={18} strokeWidth={1.75} />
        </span>
        <div>
          <p className="label-uppercase">Mon code parrainage</p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            {codes.length > 1 ? "Mes codes parrainage" : "Mon code parrainage"}
          </h2>
        </div>
      </div>

      {err ? (
        <p className="text-sm text-zinc-500 font-mono" data-testid="my-referral-error">
          Impossible de charger vos codes parrainage.
        </p>
      ) : codes.length === 0 ? (
        <p className="text-sm text-zinc-500 font-mono" data-testid="my-referral-empty">
          Aucun code parrainage n'est associé à votre profil pour le moment.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {codes.map((c) => <ReferralCard key={c.id} code={c} />)}
        </div>
      )}
    </div>
  );
}
